import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { interviewAPI } from '../services/api';

const RUBRIC_COLORS = {
  clarity: '#4F46E5',
  structure: '#0F766E',
  confidence: '#F59E0B',
  tone_delivery: '#EC4899',
  active_listening: '#8B5CF6',
  executive_presence: '#06B6D4',
  answer_relevance: '#22C55E',
};

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [interview, setInterview] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadReport(); }, [id]);

  const loadReport = async () => {
    try {
      const { data } = await interviewAPI.report(id);
      setInterview(data.interview);
      setQuestions(data.questions || []);
      setAnswers(data.answers || []);

      let parsedReport = null;
      if (data.interview.feedback_summary) {
        try {
          const dosdonts = JSON.parse(data.interview.dos_and_donts || '{}');
          const tips = JSON.parse(data.interview.improvement_tips || '[]');
          parsedReport = {
            overall_score: data.interview.overall_score,
            grade: data.interview.grade,
            feedback_summary: data.interview.feedback_summary,
            dos: dosdonts.dos || [],
            donts: dosdonts.donts || [],
            improvement_tips: tips,
          };
        } catch (e) { console.error(e); }
      }
      setReport(parsedReport);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const gradeClass = (g) => {
    if (!g) return 'grade-C';
    if (g.startsWith('A')) return 'grade-A';
    if (g.startsWith('B')) return 'grade-B';
    if (g.startsWith('C')) return 'grade-C';
    return 'grade-D';
  };

  const scoreColor = (s) => {
    const n = parseFloat(s);
    if (n >= 8) return 'var(--success)';
    if (n >= 6) return 'var(--primary)';
    if (n >= 4) return 'var(--accent)';
    return 'var(--danger)';
  };

  if (loading) return <div className="loading-screen"><span className="spinner" style={{ marginRight: 12 }} />Loading report...</div>;

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content" style={{ maxWidth: 900 }}>
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="page-title">Interview Report</div>
            <div className="page-subtitle">{interview?.job_role || 'General'} — {interview?.mode?.replace('_', ' ')} — {new Date(interview?.started_at).toLocaleDateString()}</div>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate('/history')}>← Back</button>
        </div>

        {report && (
          <>
            {/* Score Overview */}
            <div className="card card-lg" style={{ textAlign: 'center', marginBottom: 24 }}>
              <div className="score-circle" style={{ borderColor: scoreColor(report.overall_score) }}>
                <div className="score-value" style={{ color: scoreColor(report.overall_score) }}>{report.overall_score}</div>
                <div className="score-label">out of 10</div>
              </div>
              <div className={`grade-badge ${gradeClass(report.grade)}`} style={{ fontSize: 24, width: 56, height: 56, margin: '0 auto 16px' }}>
                {report.grade}
              </div>
              <p style={{ color: 'var(--text-secondary)', maxWidth: 600, margin: '0 auto', fontSize: 14, lineHeight: 1.7 }}>
                {report.feedback_summary?.substring(0, 300)}
                {report.feedback_summary?.length > 300 && '...'}
              </p>
            </div>

            {/* Scoring Rubric Bars */}
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-title" style={{ marginBottom: 20 }}>Scoring Breakdown</div>
              {[
                { key: 'clarity', label: 'Clarity (20%)', weight: 20 },
                { key: 'structure', label: 'Structure — PREP/STAR (20%)', weight: 20 },
                { key: 'confidence', label: 'Confidence (15%)', weight: 15 },
                { key: 'tone_delivery', label: 'Tone & Delivery (15%)', weight: 15 },
                { key: 'active_listening', label: 'Active Listening (10%)', weight: 10 },
                { key: 'executive_presence', label: 'Executive Presence (10%)', weight: 10 },
                { key: 'answer_relevance', label: 'Answer Relevance (10%)', weight: 10 },
              ].map(item => {
                const avgScore = answers.length > 0
                  ? (answers.reduce((s, a) => s + (parseFloat(a.score_overall) || 0), 0) / answers.length).toFixed(1)
                  : 0;
                return (
                  <div className="rubric-bar" key={item.key}>
                    <div className="rubric-label">{item.label}</div>
                    <div className="rubric-track">
                      <div className="rubric-fill" style={{ width: `${avgScore * 10}%`, background: RUBRIC_COLORS[item.key] }} />
                    </div>
                    <div className="rubric-score" style={{ color: RUBRIC_COLORS[item.key] }}>{avgScore}</div>
                  </div>
                );
              })}
            </div>

            {/* Do's and Don'ts */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              <div className="card">
                <div className="card-title" style={{ color: 'var(--success)', marginBottom: 16 }}>✅ Do's</div>
                {(report.dos || []).map((d, i) => (
                  <div key={i} className="feedback-do"><span style={{ fontSize: 14 }}>{d}</span></div>
                ))}
              </div>
              <div className="card">
                <div className="card-title" style={{ color: 'var(--danger)', marginBottom: 16 }}>❌ Don'ts</div>
                {(report.donts || []).map((d, i) => (
                  <div key={i} className="feedback-dont"><span style={{ fontSize: 14 }}>{d}</span></div>
                ))}
              </div>
            </div>

            {/* Improvement Tips */}
            {report.improvement_tips?.length > 0 && (
              <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-title" style={{ marginBottom: 16 }}>💡 Improvement Tips</div>
                {report.improvement_tips.map((tip, i) => (
                  <div key={i} className="feedback-tip" style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                      {tip.area} {tip.framework && <span className="tag tag-primary" style={{ marginLeft: 8 }}>{tip.framework}</span>}
                    </div>
                    <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{tip.suggestion}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Question-by-Question */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title" style={{ marginBottom: 20 }}>Question-by-Question Review</div>
          {questions.map((q, i) => {
            const answer = answers.find(a => a.question_id === q.id);
            return (
              <div key={q.id} style={{ marginBottom: 24, paddingBottom: 24, borderBottom: i < questions.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Q{q.question_number}. {q.question_text}</div>
                  {answer && (
                    <span style={{ padding: '2px 10px', borderRadius: 'var(--radius-full)', background: scoreColor(answer.score_overall), color: '#fff', fontSize: 12, fontWeight: 600, flexShrink: 0, marginLeft: 12 }}>
                      {answer.score_overall}/10
                    </span>
                  )}
                </div>
                <span className="tag tag-primary" style={{ marginBottom: 8, display: 'inline-block' }}>{q.category}</span>
                {answer ? (
                  <>
                    <div style={{ background: 'var(--bg)', padding: 12, borderRadius: 'var(--radius-sm)', marginTop: 8, fontSize: 14 }}>
                      <strong>Your answer:</strong> {answer.answer_text || 'No answer'}
                    </div>
                    {answer.feedback && (
                      <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                        <strong>Feedback:</strong> {answer.feedback}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>Not answered</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: '16px 0' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/history')}>View History</button>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/interview/setup')}>Practice Again →</button>
        </div>
      </main>
    </div>
  );
}
